import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Box } from '@mantine/core';
import React from 'react';
import type { RefObject } from 'react';

export interface ScrollContainerHandle {
  scrollToSection: (ref: RefObject<HTMLDivElement>) => void;
}

interface ScrollContainerProps {
  topShadowOffset?: number;
  bottomShadowOffset?: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

function useScrollableSections() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  };

  const scrollToSection = (ref: RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  return {
    scrollRef,
    showTopShadow,
    showBottomShadow,
    handleScroll,
    scrollToSection,
  };
}

export const ScrollContainer = forwardRef<
  ScrollContainerHandle,
  ScrollContainerProps
>(
  (
    { topShadowOffset = 105, bottomShadowOffset = 70, style, children },
    ref,
  ) => {
    const {
      scrollRef,
      showTopShadow,
      showBottomShadow,
      handleScroll,
      scrollToSection,
    } = useScrollableSections();

    useImperativeHandle(ref, () => ({ scrollToSection }));

    return (
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto', ...style }}
      >
        {showTopShadow && (
          <div
            style={{
              position: 'absolute',
              width: 659,
              top: topShadowOffset,
              left: 0,
              right: 0,
              height: 15,
              background:
                'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
        {children}
        {showBottomShadow && (
          <div
            style={{
              position: 'absolute',
              width: 659,
              bottom: bottomShadowOffset,
              left: 0,
              right: 0,
              height: 15,
              background:
                'linear-gradient(to top, rgba(0, 0, 0, 0.1), transparent)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        )}
      </Box>
    );
  },
);
